import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import UnassignTranscriptSpeaker from '@Application/Beneficiaries/Transcripts/Commands/UnassignTranscriptSpeaker';
import unassignTranscriptSpeaker from '@Application/Beneficiaries/Transcripts/unassignTranscriptSpeaker';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import { setSpeakerAssignmentContext } from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewDetails/SpeakerAssignmentSidebar/Signals/speakerAssignmentSignal';
import type TranscriptSpeakerView from '@UI/Store/Interviews/Views/TranscriptSpeakerView';
import { css } from '@emotion/react';
import { UserX } from 'lucide-react';
import * as React from 'react';
import { useDispatch } from '@verclam/synapse';
import { Button, confirmDialog } from '@verclam/ui-kit';

const OTHER_KIND = 'other';

const buttonStyle = css`
	width: 100%;
`;

type Props = {
	readonly facilityId: number;
	readonly subject: SubjectAddress;
	readonly interviewId: number;
	readonly speaker: TranscriptSpeakerView;
};

/**
 * Removing a `kind = OTHER` attribution destroys a name the relecteur typed by hand, with nothing
 * else to reconstruct it — a `confirmDialog` guards it. Every other `kind` is recomposed by the
 * server from an entity it still holds, so removing it needs no confirmation (§9.5): a systematic
 * confirmation would become a reflex click, not a real second intention.
 */
const SpeakerAssignmentUnassignAction: React.FC<Props> = ({ facilityId, subject, interviewId, speaker }) => {
	const dispatch = useDispatch();

	const handleUnassign = React.useCallback(() => {
		dispatch(
			unassignTranscriptSpeaker(
				new UnassignTranscriptSpeaker(facilityId, subject, interviewId, speaker.id),
			),
		)
			.then(() => setSpeakerAssignmentContext(null))
			.catch(ignoreHandledError);
	}, [dispatch, facilityId, subject, interviewId, speaker.id]);

	const handleClick = React.useCallback(() => {
		if (speaker.kind.value === OTHER_KIND) {
			confirmDialog({
				header: "Retirer l'attribution",
				message: (
					<React.Fragment>
						Le nom « {speaker.name} » sera perdu. Retirer l'attribution ?
					</React.Fragment>
				),
				acceptLabel: 'Retirer',
				acceptSeverity: 'danger',
				accept: handleUnassign,
			});
			return;
		}

		handleUnassign();
	}, [speaker.kind.value, speaker.name, handleUnassign]);

	if (!speaker.isAssigned) {
		return null;
	}

	return (
		<Button
			type="button"
			label="Retirer l'attribution"
			severity="danger"
			outlined
			icon={<UserX size={16} />}
			onClick={handleClick}
			css={buttonStyle}
		/>
	);
};

export default SpeakerAssignmentUnassignAction;
